/**
 * countries.ts — ISO 3166-1 alpha-2 codes plus UK constituents,
 * names, and flag emoji.
 *
 * Flag rendering uses Unicode regional-indicator pairs for ISO
 * countries and tag-letter sequences for UK constituents (England,
 * Scotland, Wales). No assets, no deps, native emoji renders the
 * flag everywhere.
 *
 * UK constituents are first-class entries because golfer identity
 * splits along those lines — a Welsh player rarely calls themselves
 * "British" on a leaderboard. They use 5-char codes (GB-WLS etc.)
 * and live alongside the ISO 3166-1 alpha-2 codes in the same list.
 * Storage layer (`profiles.country`) is a TEXT column, so the
 * extra length is fine.
 *
 * Northern Ireland is intentionally NOT in the list: there is no
 * widely-accepted, non-political flag for it. A user there can
 * pick "United Kingdom" or "Ireland" depending on identity.
 */

export type CountryCode = string;

export type Country = {
  code: CountryCode;
  name: string;
  /** Alternative names that should match in typeahead search. */
  aliases?: string[];
};

// ── UK constituents — special-case emoji via tag sequences ───────────
// Format: BLACK FLAG (U+1F3F4) + tag letters spelling the subdivision
// + CANCEL TAG (U+E007F). Stored as fully-formed strings so we don't
// re-derive on every render.
const TAG_FLAGS: Record<string, string> = {
  'GB-ENG': '\u{1F3F4}\u{E0067}\u{E0062}\u{E0065}\u{E006E}\u{E0067}\u{E007F}', // 🏴󠁧󠁢󠁥󠁮󠁧󠁿
  'GB-SCT': '\u{1F3F4}\u{E0067}\u{E0062}\u{E0073}\u{E0063}\u{E0074}\u{E007F}', // 🏴󠁧󠁢󠁳󠁣󠁴󠁿
  'GB-WLS': '\u{1F3F4}\u{E0067}\u{E0062}\u{E0077}\u{E006C}\u{E0073}\u{E007F}', // 🏴󠁧󠁢󠁷󠁬󠁳󠁿
};

/**
 * Flag emoji from a country code. Handles both ISO 3166-1 alpha-2
 * (e.g. "GB", "DE") and UK-constituent codes ("GB-WLS"). Returns ""
 * for falsy / malformed input so callers can render conditionally
 * without guarding.
 */
export function flagFromCode(code: string | null | undefined): string {
  if (!code) return '';
  const upper = code.toUpperCase();
  if (TAG_FLAGS[upper]) return TAG_FLAGS[upper];
  if (upper.length !== 2) return '';
  const base = 0x1f1e6;
  const a = upper.charCodeAt(0);
  const b = upper.charCodeAt(1);
  if (a < 0x41 || a > 0x5a || b < 0x41 || b > 0x5a) return '';
  return String.fromCodePoint(base + (a - 0x41), base + (b - 0x41));
}

// ── Country list ─────────────────────────────────────────────────────
// Pinned golfing nations first, then UK constituents (so a Welsh
// player finds Wales near the top), then the full ISO 3166-1 list
// alphabetised. Aliases cover common alternative names ("US" → "USA",
// "Czech Republic" → "Czechia") so typeahead matches what people
// actually type.

export const COUNTRIES: ReadonlyArray<Country> = [
  // Pinned — biggest golfing nations, recognisable to almost everyone.
  { code: 'GB', name: 'United Kingdom', aliases: ['UK', 'Britain', 'Great Britain'] },
  { code: 'IE', name: 'Ireland' },
  { code: 'US', name: 'United States', aliases: ['USA', 'America', 'United States of America'] },
  { code: 'AU', name: 'Australia' },
  { code: 'CA', name: 'Canada' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'ZA', name: 'South Africa' },

  // UK constituents — pinned right under the UK so the proximity is
  // obvious to a Welsh / Scottish / English player scrolling the list.
  { code: 'GB-ENG', name: 'England' },
  { code: 'GB-SCT', name: 'Scotland' },
  { code: 'GB-WLS', name: 'Wales', aliases: ['Cymru'] },

  // Alphabetical thereafter — full ISO 3166-1 alpha-2 list.
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AO', name: 'Angola' },
  { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AW', name: 'Aruba' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BM', name: 'Bermuda' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BR', name: 'Brazil' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CV', name: 'Cape Verde', aliases: ['Cabo Verde'] },
  { code: 'KY', name: 'Cayman Islands' },
  { code: 'CF', name: 'Central African Republic' },
  { code: 'TD', name: 'Chad' },
  { code: 'CL', name: 'Chile' },
  { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' },
  { code: 'KM', name: 'Comoros' },
  { code: 'CG', name: 'Congo' },
  { code: 'CD', name: 'Congo (DRC)', aliases: ['Democratic Republic of the Congo'] },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CW', name: 'Curaçao' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic', aliases: ['Czechia', 'Czech'] },
  { code: 'CI', name: "Côte d'Ivoire", aliases: ['Ivory Coast'] },
  { code: 'DK', name: 'Denmark' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DM', name: 'Dominica' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EG', name: 'Egypt' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'GQ', name: 'Equatorial Guinea' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'EE', name: 'Estonia' },
  { code: 'SZ', name: 'Eswatini', aliases: ['Swaziland'] },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FJ', name: 'Fiji' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GE', name: 'Georgia' },
  { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GI', name: 'Gibraltar' },
  { code: 'GR', name: 'Greece' },
  { code: 'GL', name: 'Greenland' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GU', name: 'Guam' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GG', name: 'Guernsey' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HT', name: 'Haiti' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IR', name: 'Iran' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IM', name: 'Isle of Man' },
  { code: 'IL', name: 'Israel' },
  { code: 'IT', name: 'Italy' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'JP', name: 'Japan' },
  { code: 'JE', name: 'Jersey' },
  { code: 'JO', name: 'Jordan' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'XK', name: 'Kosovo' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'LA', name: 'Laos' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LY', name: 'Libya' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MO', name: 'Macau', aliases: ['Macao'] },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MV', name: 'Maldives' },
  { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malta' },
  { code: 'MH', name: 'Marshall Islands' },
  { code: 'MR', name: 'Mauritania' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'MX', name: 'Mexico' },
  { code: 'FM', name: 'Micronesia' },
  { code: 'MD', name: 'Moldova' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'MM', name: 'Myanmar', aliases: ['Burma'] },
  { code: 'NA', name: 'Namibia' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NL', name: 'Netherlands', aliases: ['Holland'] },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KP', name: 'North Korea' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'NO', name: 'Norway' },
  { code: 'OM', name: 'Oman' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PW', name: 'Palau' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PA', name: 'Panama' },
  { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'PE', name: 'Peru' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russia' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'WS', name: 'Samoa' },
  { code: 'SM', name: 'San Marino' },
  { code: 'ST', name: 'São Tomé and Príncipe' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SN', name: 'Senegal' },
  { code: 'RS', name: 'Serbia' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SB', name: 'Solomon Islands' },
  { code: 'SO', name: 'Somalia' },
  { code: 'KR', name: 'South Korea' },
  { code: 'SS', name: 'South Sudan' },
  { code: 'ES', name: 'Spain' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SY', name: 'Syria' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TJ', name: 'Tajikistan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TL', name: 'Timor-Leste', aliases: ['East Timor'] },
  { code: 'TG', name: 'Togo' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinidad and Tobago' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turkey', aliases: ['Türkiye'] },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TC', name: 'Turks and Caicos' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'UG', name: 'Uganda' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'AE', name: 'United Arab Emirates', aliases: ['UAE'] },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'VA', name: 'Vatican City', aliases: ['Holy See'] },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'YE', name: 'Yemen' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' },
];

const CODE_TO_NAME = new Map(COUNTRIES.map((c) => [c.code, c.name]));

export function nameFromCode(code: string | null | undefined): string | null {
  if (!code) return null;
  return CODE_TO_NAME.get(code.toUpperCase()) ?? null;
}

// Lookup table for codeFromAny: lowercased name + every alias →
// canonical code. Built once at module load. Includes UK constituent
// names (England, Scotland, Wales) mapping to GB so a free-form
// "Scotland" round still aggregates with the rest of the UK pile.
const NAME_TO_CODE = (() => {
  const m = new Map<string, string>();
  for (const c of COUNTRIES) {
    m.set(c.name.toLowerCase(), c.code);
    for (const alias of c.aliases ?? []) m.set(alias.toLowerCase(), c.code);
  }
  // UK sub-codes already exist, but the public flag set only has GB
  // so we collapse for tally purposes.
  m.set('england', 'GB');
  m.set('scotland', 'GB');
  m.set('wales', 'GB');
  m.set('northern ireland', 'GB');
  m.set('cymru', 'GB');
  return m;
})();

// Coerce any country value (ISO code, full name, alias, or
// constituent country) to a canonical alpha-2 ISO code. Returns null
// when the input is empty or unrecognised — caller decides whether to
// surface as "Unknown" or skip the row.
export function codeFromAny(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^[A-Za-z]{2}$/.test(trimmed)) return trimmed.toUpperCase();
  // Subdivision codes (GB-WLS, GB-ENG, …) collapse to their parent
  // alpha-2 for tally purposes — the breakdown shows one UK pile, and a
  // course stored as "GB-WLS" must not read back as country-less (which
  // would keep it stuck in the "missing countries" backfill panel).
  const sub = trimmed.match(/^([A-Za-z]{2})-[A-Za-z]{2,3}$/);
  if (sub) return sub[1].toUpperCase();
  const hit = NAME_TO_CODE.get(trimmed.toLowerCase());
  return hit ?? null;
}
